import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BoxedTitle from "@/components/BoxedTitle";
import useAdminReferences from "@/pages/admin/references/useAdminReferences";
import { clearCache } from "@/pages/admin/adminCache";

const KEY_STORAGE = "vbb_admin_key";

const TYPE_LABELS = {
  webpage: "Webpage",
  book: "Book",
  chapter: "Book chapter",
  "article-journal": "Journal article",
  "article-magazine": "Magazine article",
  "article-newspaper": "News article",
  motion_picture: "Video / film",
  interview: "Interview",
  "post-weblog": "Blog post",
  speech: "Speech / talk",
};

/**
 * /admin/references — top-level bibliographic library. Each row shows
 * the CSL type, title, author(s), and inline APA preview (rendered
 * client-side via the /format endpoint on demand).
 */
export default function AdminReferences() {
  const navigate = useNavigate();
  const {
    key,
    references,
    status,
    deleteReference,
    formatReference,
  } = useAdminReferences();
  const [filter, setFilter] = useState("all");
  const [previewStyle, setPreviewStyle] = useState("apa"); // apa|mla
  const [previews, setPreviews] = useState({}); // id -> string

  useEffect(() => {
    if (!key || status.state === "unauthorized") {
      navigate("/login?next=/admin/references", { replace: true });
    }
  }, [key, status.state, navigate]);

  // Re-fetch previews whenever list or style changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = {};
      for (const r of references) {
        const cite = await formatReference(r.id, previewStyle);
        if (cancelled) return;
        next[r.id] = cite;
      }
      if (!cancelled) setPreviews(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [references, previewStyle, formatReference]);

  const onLock = () => {
    sessionStorage.removeItem(KEY_STORAGE);
    clearCache();
    navigate("/login?next=/admin/references", { replace: true });
  };

  const filtered = references.filter((r) => {
    if (filter === "published") return r.published;
    if (filter === "draft") return !r.published;
    if (filter === "archived") return !!r.full_text;
    return true;
  });

  if (!key) return null;

  return (
    <main
      className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-32"
      data-testid="admin-references-page"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="vbb-overline">
            <Link to="/admin" className="vbb-link" data-testid="crumb-admin">
              Admin
            </Link>{" "}
            / References
          </div>
          <h1
            className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl mt-6 tracking-tighter leading-[0.95] pl-[0.35em]"
            data-testid="admin-references-title"
          >
            <BoxedTitle size="lg" thick>
              References.
            </BoxedTitle>
          </h1>
          <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-[#6b6b6b]">
            Bibliographic library. Stored as CSL-JSON so any entry can
            be exported into an essay in APA or MLA form.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            className="vbb-btn vbb-btn-ghost"
            data-testid="admin-back-to-home"
          >
            ← Admin
          </Link>
          <Link
            to="/admin/essays"
            className="vbb-btn vbb-btn-ghost"
            data-testid="references-open-essays"
          >
            Essays
          </Link>
          <Link
            to="/admin/references/new"
            className="vbb-btn"
            data-testid="references-new-btn"
          >
            New reference
          </Link>
          <button
            onClick={onLock}
            className="vbb-btn vbb-btn-ghost"
            data-testid="admin-lock-btn"
          >
            Lock
          </button>
        </div>
      </div>

      <div
        className="mt-8 flex flex-wrap items-center gap-4"
        data-testid="reference-filters-row"
      >
        <div className="flex flex-wrap gap-2">
          {[
            { id: "all", label: `All (${references.length})` },
            {
              id: "draft",
              label: `Drafts (${
                references.filter((r) => !r.published).length
              })`,
            },
            {
              id: "published",
              label: `Recommended (${
                references.filter((r) => r.published).length
              })`,
            },
            {
              id: "archived",
              label: `Archived (${
                references.filter((r) => !!r.full_text).length
              })`,
            },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`reference-filter-${f.id}`}
              className={`px-4 py-2 font-mono-tiny uppercase tracking-wider border ${
                filter === f.id
                  ? "bg-[#0a0a0a] text-white border-[#0a0a0a]"
                  : "bg-white text-[#0a0a0a] border-[#0a0a0a] hover:bg-[#fafafa]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2 font-mono-tiny">
          <span className="text-[#6b6b6b]">Preview format:</span>
          {["apa", "mla"].map((s) => (
            <button
              key={s}
              onClick={() => setPreviewStyle(s)}
              data-testid={`reference-style-${s}`}
              className={`px-3 py-1 uppercase tracking-wider border ${
                previewStyle === s
                  ? "bg-[#0a0a0a] text-white border-[#0a0a0a]"
                  : "bg-white text-[#0a0a0a] border-[#0a0a0a] hover:bg-[#fafafa]"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {status.state === "error" && (
        <div
          className="mt-8 p-4 border border-[#e34234] text-[#e34234] font-mono-tiny"
          data-testid="admin-error-banner"
        >
          {status.error}
        </div>
      )}

      <div className="mt-8" data-testid="reference-list">
        {status.state === "ready" && filtered.length === 0 && (
          <div
            className="p-10 text-center font-mono-tiny text-[#6b6b6b] border border-[#0a0a0a]"
            data-testid="reference-empty-state"
          >
            {references.length === 0
              ? "No references yet."
              : "No references match this filter."}
          </div>
        )}
        {filtered.map((ref) => {
          const csl = ref.csl || {};
          const typeLabel = TYPE_LABELS[csl.type] || csl.type || "webpage";
          const preview = previews[ref.id] || "…";
          return (
            <article
              key={ref.id}
              className="border border-[#0a0a0a] p-6 bg-white mb-4"
              data-testid={`reference-row-${ref.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono-tiny uppercase tracking-wider text-[#6b6b6b]">
                      {typeLabel}
                    </span>
                    {ref.published && (
                      <span
                        className="px-2 py-0.5 font-mono-tiny uppercase tracking-wider bg-[#e34234] text-white"
                        data-testid={`reference-recommended-badge-${ref.id}`}
                      >
                        Recommended
                      </span>
                    )}
                    {ref.full_text && (
                      <span
                        className="px-2 py-0.5 font-mono-tiny uppercase tracking-wider bg-[#0a0a0a] text-white"
                        data-testid={`reference-archived-badge-${ref.id}`}
                      >
                        Archived
                      </span>
                    )}
                  </div>
                  <h2 className="font-display text-xl mt-2 leading-tight">
                    <Link
                      to={`/admin/references/${ref.id}`}
                      className="hover:text-[#e34234] transition-colors"
                      data-testid={`reference-edit-link-${ref.id}`}
                    >
                      {csl.title || "(untitled)"}
                    </Link>
                  </h2>
                  <p
                    className="mt-3 text-[14px] leading-relaxed text-[#2a2a2a] font-mono-tiny break-words"
                    data-testid={`reference-citation-${ref.id}`}
                  >
                    {preview}
                  </p>
                  {ref.notes && (
                    <p className="mt-3 text-[13px] leading-relaxed text-[#6b6b6b] whitespace-pre-line">
                      {ref.notes}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link
                    to={`/admin/references/${ref.id}`}
                    className="vbb-btn vbb-btn-ghost"
                    data-testid={`reference-open-btn-${ref.id}`}
                  >
                    Edit
                  </Link>
                  <button
                    type="button"
                    onClick={async () => {
                      if (
                        window.confirm(
                          `Delete "${csl.title || "this reference"}"?\nIt will be removed from any essay that cites it.`
                        )
                      ) {
                        await deleteReference(ref.id);
                      }
                    }}
                    className="vbb-btn vbb-btn-ghost text-[#e34234] border-[#e34234]"
                    data-testid={`reference-delete-btn-${ref.id}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </main>
  );
}
